<template>
  <div class="practice-assistant-form-re">
    <div v-loading="detailLoading" class="practice-assistant-form-re__content">
      <section class="practice-assistant-form-re__section">
        <div class="practice-assistant-form-re__section-title">基本信息</div>

        <div class="practice-assistant-form-re__field">
          <div class="practice-assistant-form-re__field-label">
            <span class="practice-assistant-form-re__required">*</span>
            <span>本次任务派发给谁</span>
          </div>
          <div class="practice-assistant-form-re__field-control">
            <PracticePersonSelect v-model:range-value="userRangePojoList" />
          </div>
        </div>

        <div class="practice-assistant-form-re__field">
          <div class="practice-assistant-form-re__field-label">
            <span class="practice-assistant-form-re__required">*</span>
            <span>任务截止时间</span>
          </div>
          <div class="practice-assistant-form-re__field-control">
            <el-date-picker
              v-model="dueTime"
              class="practice-assistant-form-re__date-picker"
              :clearable="false"
              :disabled-date="disabledEndDate"
              format="YYYY-MM-DD HH:mm"
              placeholder="请选择任务截止时间"
              style="width: 100% !important"
              type="datetime"
              value-format="X"
            />
          </div>
        </div>
      </section>

      <section
        class="practice-assistant-form-re__section practice-assistant-form-re__section--profiles"
      >
        <div class="practice-assistant-form-re__section-title">客户画像</div>

        <div v-if="customerProfileList.length" class="practice-assistant-form-re__profile-list">
          <div
            v-for="profile in customerProfileList"
            :key="profile.id"
            class="practice-assistant-form-re__profile-card"
            :class="{
              'is-selected': isProfileSelected(profile.id)
            }"
          >
            <div class="practice-assistant-form-re__profile-header">
              <div class="practice-assistant-form-re__profile-info">
                <div class="practice-assistant-form-re__profile-name">
                  {{ profile.customerName }}
                </div>
                <div class="practice-assistant-form-re__profile-summary">
                  {{ profile.profile || '暂无画像摘要' }}
                </div>
              </div>
              <i
                class="practice-assistant-form-re__profile-expand t-iconfont"
                :class="
                  expandedProfileIds.includes(profile.id)
                    ? 'icon-arrow-up-s-line'
                    : 'icon-arrow-down-s-line'
                "
                @click="toggleProfileExpanded(profile.id)"
              ></i>
            </div>

            <div
              v-show="expandedProfileIds.includes(profile.id)"
              class="practice-assistant-form-re__profile-body"
            >
              <!-- <div class="practice-assistant-form-re__profile-desc">
                {{ profile.profile || '暂无场景描述' }}
              </div> -->

              <div class="practice-assistant-form-re__profile-section">
                <div class="practice-assistant-form-re__profile-section-title">陪练内容</div>
                <div class="practice-assistant-form-re__profile-field">
                  <div class="practice-assistant-form-re__profile-field-label">场景描述</div>
                  <div class="practice-assistant-form-re__profile-field-value">
                    {{ profile.background || '暂无场景描述' }}
                  </div>
                </div>
                <div class="practice-assistant-form-re__profile-field">
                  <div class="practice-assistant-form-re__profile-field-label">训练目标</div>
                  <div class="practice-assistant-form-re__profile-field-value">
                    {{ profile.trainingGoal || '暂无训练目标' }}
                  </div>
                </div>
              </div>

              <div class="practice-assistant-form-re__profile-section">
                <div class="practice-assistant-form-re__profile-section-title">客户信息</div>
                <div class="practice-assistant-form-re__profile-field">
                  <div class="practice-assistant-form-re__profile-field-label">客户原话</div>
                  <div class="practice-assistant-form-re__profile-field-value">
                    {{ profile.customerQuote || '暂无客户原话' }}
                  </div>
                </div>
                <div class="practice-assistant-form-re__profile-field">
                  <div class="practice-assistant-form-re__profile-field-label">客户初始态度</div>
                  <div class="practice-assistant-form-re__profile-field-value">
                    {{ profile.initialAttitude || '暂无客户初始态度' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="practice-assistant-form-re__empty">暂无客户画像</div>
      </section>
    </div>

    <div class="practice-assistant-form-re__footer">
      <el-button class="practice-assistant-form-re__btn" @click="emit('cancel')">取消</el-button>
      <el-button
        class="practice-assistant-form-re__btn"
        color="#7a52f4"
        :loading="buttonLoading"
        @click="handleSave"
      >
        确定
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import PracticePersonSelect from '../person-select/index.vue'
import { createThemePracticeTask, getThemePracticeTaskDetail } from '@/api/index.js'

const createDefaultForm = () => ({
  trainingContent: '',
  supplementFile: [],
  themeName: '',
  userRangePojoList: [],
  themeDesc: '',
  dueTime: '',
  supplementSummaryFileUrl: '',
  customerProfileList: [],
  selectedProfileIds: []
})

defineOptions({
  name: 'PracticeAssistantFormRe'
})

const props = defineProps({
  params: {
    type: Object,
    default: () => ({})
  },
  saveLoading: {
    type: Boolean,
    default: false
  },
  autoLoadDetail: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:form', 'save', 'cancel', 'loaded', 'success'])

const innerForm = ref(createDefaultForm())
const detailLoading = ref(false)
const submitLoading = ref(false)
const expandedProfileIds = ref([])
let detailRequestToken = 0

const buttonLoading = computed(
  () => props.saveLoading || submitLoading.value || detailLoading.value
)
const sourceTaskId = computed(() => props.params?.id || innerForm.value.id || '')

const getRangeFallbackName = (item = {}) => {
  const rangeTypeText = item.property === 'dept' ? '部门' : '成员'
  return item.id ? `${rangeTypeText}(${item.id})` : rangeTypeText
}

const normalizeUserRange = (userRange) => {
  if (!Array.isArray(userRange)) {
    return []
  }

  return userRange
    .map((item) => {
      const id = item?.id || item?.userId || item?.deptId || ''
      const property = item?.property || 'user'

      return {
        id,
        name:
          item?.name ||
          item?.userName ||
          item?.deptName ||
          item?.label ||
          getRangeFallbackName({ id, property }),
        property
      }
    })
    .filter((item) => item.id)
}

const getProfileId = (profile, index = 0) => String(profile?.profileNo || index + 1)

const normalizeCustomerProfileList = (profileList) => {
  if (!Array.isArray(profileList)) {
    return []
  }

  return profileList
    .map((item, index) => {
      if (!item) {
        return null
      }

      const customerProfile = {
        profileNo: Number(item.profileNo || index + 1),
        customerName: String(item.customerName || `客户画像${index + 1}`),
        background: String(item.background || ''),
        initialAttitude: String(item.initialAttitude || ''),
        trainingGoal: String(item.trainingGoal || ''),
        profile: String(item.profile || ''),
        customerQuote: String(item.customerQuote || ''),
        id: getProfileId(item, index)
      }

      if (item.meetingCount !== undefined) {
        customerProfile.meetingCount = item.meetingCount
      }

      return customerProfile
    })
    .filter(Boolean)
}

const normalizeSelectedProfileIds = (selectedProfileIds) => {
  return Array.isArray(selectedProfileIds)
    ? selectedProfileIds.map((item) => String(item)).filter(Boolean)
    : []
}

const normalizeForm = (formValue = {}) => {
  const customerProfileList = normalizeCustomerProfileList(formValue.customerProfileList)
  const selectedProfileIds = normalizeSelectedProfileIds(formValue.selectedProfileIds)

  return {
    ...createDefaultForm(),
    ...formValue,
    userRangePojoList: normalizeUserRange(formValue.userRangePojoList),
    dueTime: formValue.dueTime || '',
    customerProfileList,
    selectedProfileIds: selectedProfileIds.length
      ? selectedProfileIds
      : customerProfileList.map((profile) => profile.id)
  }
}

const buildFormFromDetail = (detail = {}) => {
  return normalizeForm({
    ...detail,
    userRangePojoList: detail.userRangePojoList,
    dueTime: detail.dueTime || '',
    customerProfileList: detail.customerProfileList
  })
}

const applyForm = (nextForm) => {
  innerForm.value = normalizeForm(nextForm)
}

const syncForm = (nextForm) => {
  applyForm(nextForm)
  emit('update:form', innerForm.value)
}

const updateForm = (patch) => {
  syncForm({
    ...innerForm.value,
    ...patch
  })
}

const userRangePojoList = computed({
  get: () => innerForm.value.userRangePojoList || [],
  set: (value) => {
    updateForm({ userRangePojoList: value })
  }
})

const dueTime = computed({
  get: () => innerForm.value.dueTime || '',
  set: (value) => {
    updateForm({ dueTime: value ? Number(value) : '' })
  }
})

const customerProfileList = computed(() => innerForm.value.customerProfileList || [])

const selectedProfileIds = computed({
  get: () => innerForm.value.selectedProfileIds || [],
  set: (value) => {
    updateForm({ selectedProfileIds: value })
  }
})

const disabledEndDate = (date) => {
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  return date.getTime() < todayStart.getTime()
}

const isProfileSelected = (profileId) => {
  return selectedProfileIds.value.includes(String(profileId))
}

const toggleProfileExpanded = (profileId) => {
  const normalizedProfileId = String(profileId || '')

  if (!normalizedProfileId) {
    return
  }

  if (expandedProfileIds.value.includes(normalizedProfileId)) {
    expandedProfileIds.value = expandedProfileIds.value.filter(
      (item) => item !== normalizedProfileId
    )
    return
  }

  expandedProfileIds.value = [...expandedProfileIds.value, normalizedProfileId]
}

const getSelectedProfileList = () => {
  const selectedIdSet = new Set(normalizeSelectedProfileIds(selectedProfileIds.value))
  return normalizeCustomerProfileList(customerProfileList.value).filter((item) =>
    selectedIdSet.has(item.id)
  )
}

const buildCustomerProfilePayload = () => {
  return getSelectedProfileList().map(({ id, ...profile }) => profile)
}

const validateForm = () => {
  if (!normalizeUserRange(userRangePojoList.value).length) {
    ElMessage.warning('请选择本次任务派发给谁')
    return false
  }

  if (!dueTime.value) {
    ElMessage.warning('请选择任务截止时间')
    return false
  }

  if (!getSelectedProfileList().length) {
    ElMessage.warning('请至少选择 1 个客户画像')
    return false
  }

  if (!sourceTaskId.value) {
    ElMessage.warning('未获取到任务信息，请稍后重试')
    return false
  }

  return true
}

const buildSubmitPayload = () => {
  return {
    sourceTaskId: sourceTaskId.value,
    userRangePojoList: normalizeUserRange(userRangePojoList.value),
    dueTime: dueTime.value,
    customerProfileList: buildCustomerProfilePayload()
  }
}

const fetchDetail = async () => {
  if (!props.autoLoadDetail || !props.params?.id) {
    return
  }

  const currentToken = ++detailRequestToken
  detailLoading.value = true

  try {
    const res = await getThemePracticeTaskDetail(props.params)

    if (currentToken !== detailRequestToken) {
      return
    }

    const detail = res.result || {}
    const nextForm = buildFormFromDetail(detail)

    syncForm(nextForm)
    emit('loaded', {
      detail,
      form: nextForm
    })
  } finally {
    if (currentToken === detailRequestToken) {
      detailLoading.value = false
    }
  }
}

const handleSave = async () => {
  if (buttonLoading.value || !validateForm()) {
    return
  }

  const payload = buildSubmitPayload()
  submitLoading.value = true

  try {
    await createThemePracticeTask(payload)
    ElMessage.success('再次派发成功')
    emit('save', payload)
    emit('success', payload)
  } finally {
    submitLoading.value = false
  }
}

watch(
  () => props.params?.id,
  () => {
    detailRequestToken += 1
    fetchDetail()
  },
  {
    immediate: true
  }
)

watch(
  customerProfileList,
  (currentProfileList) => {
    if (!currentProfileList.length) {
      expandedProfileIds.value = []
      return
    }

    expandedProfileIds.value = [currentProfileList[0].id]
  },
  {
    immediate: true
  }
)
</script>

<style lang="scss" scoped>
// 文字/Text-4 强调正文标题
$text-strong: #323233;
// 文字/Text-3 次强调正文标题
$text-secondary: #646566;
// 文字/Text-2 次要信息
$text-placeholder: #969799;
// 线条/Line-1 浅线条
$line-light: #f2f3f5;
// 线条/Line-2 一般线条
$line-normal: #e5e6eb;
// 拓展色/紫罗兰/Violets-6
$violet-6: #7a52f4;
// 填充/纯白
$fill-white: #ffffff;
// 填充/Fill-1 浅色 禁用背景
$fill-soft: #f7f8fa;
// 错误色/Error-6 常规
$error-6: #f54e4e;
// 阴影
$footer-shadow: 0 2px 15px 2px rgba(0, 0, 0, 0.05);

.practice-assistant-form-re {
  margin: 0 -24px;

  &__content {
    padding: 0 24px 24px;
  }

  &__section + &__section {
    margin-top: 24px;
  }

  &__section-title {
    position: relative;
    padding-left: 12px;
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: $text-strong;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 3px;
      width: 4px;
      height: 16px;
      border-radius: 4px;
      background: $violet-6;
    }
  }

  &__field + &__field {
    margin-top: 12px;
  }

  &__field-label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 20px;
    color: $text-strong;
  }

  &__required {
    margin-right: 4px;
    color: $error-6;
  }

  &__field-control {
    width: 480px;
    max-width: 100%;
  }

  &__date-picker {
    width: 100%;
  }

  &__profile-list {
    padding-bottom: 4px;
  }

  &__profile-card {
    border: 1px solid $line-normal;
    border-radius: 12px;
    background: $fill-white;
    overflow: hidden;

    & + & {
      margin-top: 12px;
    }

    &.is-selected {
      border-color: rgba(114, 46, 209, 0.4);
    }
  }

  &__profile-header {
    display: flex;
    align-items: center;
    padding: 12px;
  }

  &__profile-info {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
  }

  &__profile-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $text-strong;
  }

  &__profile-summary {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $text-placeholder;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__profile-expand {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 20px;
    color: $text-secondary;
    cursor: pointer;
  }

  &__profile-body {
    padding: 0 12px;
  }

  &__profile-desc {
    padding-top: 12px;
    border-top: 1px dashed $line-normal;
    margin-bottom: 16px;
    font-size: 13px;
    line-height: 18px;
    color: $text-secondary;
  }

  &__profile-section {
    padding: 12px;
    border-radius: 16px;
    background: $fill-soft;

    & + & {
      margin-top: 16px;
    }
  }

  &__profile-section-title {
    position: relative;
    padding-left: 12px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: $text-strong;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 3px;
      width: 4px;
      height: 14px;
      border-radius: 4px;
      background: $violet-6;
    }
  }

  &__profile-field {
    padding: 8px 12px;
    border-radius: 12px;
    background: $fill-white;

    & + & {
      margin-top: 8px;
    }
  }

  &__profile-field-label {
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: $text-strong;
  }

  &__profile-field-value {
    font-size: 13px;
    line-height: 18px;
    color: $text-secondary;
  }

  &__empty {
    padding: 48px 0;
    border: 1px dashed $line-normal;
    border-radius: 12px;
    text-align: center;
    font-size: 14px;
    line-height: 20px;
    color: $text-placeholder;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid $line-light;
    background: $fill-white;
    box-shadow: $footer-shadow;
  }

  &__btn {
    height: 36px;
    padding: 8px 20px;
    border-radius: 12px;

    & + & {
      margin-left: 12px;
    }
  }

  :deep(.el-input__wrapper) {
    border-radius: 8px;
  }
}
</style>
